import dayjs from 'dayjs';
import { DropdownOptionType } from '../../../../components/Dropdown/Type';
import { BulkShipmentType, BoxType } from '../../../../types/shipment';
import { ShipmentUserSchema } from '../../UserDetails/Schema';
import { productDescriptionSchema } from '../../ShipmentDetails/ProductDescription/schema';

export const validateAddressJoi = (addr: any) => {
  const cleanAddr = {
    ...addr,
    phoneNumber: addr.phoneNumber ? Number(addr.phoneNumber) : undefined,
    telephoneCode: addr.telephoneCode ? Number(addr.telephoneCode) : undefined,
  };
  const { error } = ShipmentUserSchema.validate(cleanAddr, { abortEarly: false });
  if (!error) return [];
  return error.details.map((d: any) => d.path[0] as string);
};

export const validateProductJoi = (prod: any) => {
  const cleanProd = {
    ...prod,
    quantity: prod.quantity ? Number(prod.quantity) : undefined,
    unitValue: prod.unitValue ? Number(prod.unitValue) : undefined,
  };
  const { error } = productDescriptionSchema.validate(cleanProd, { abortEarly: false });
  if (!error) return [];
  return error.details.map((d: any) => d.path[0] as string);
};

export const validateShipmentFields = (shipment: any) => {
  const shipperErrors = validateAddressJoi(shipment.shipper);
  const consigneeErrors = validateAddressJoi(shipment.consignee);

  const p = shipment.package || {};
  const packageErrors: string[] = [];
  if (!p.unitOfMeasurement?.trim()) packageErrors.push('unitOfMeasurement');
  if (!p.currency?.trim()) packageErrors.push('currency');
  if (!p.shipmentMode?.trim()) packageErrors.push('shipmentMode');

  const isExport = shipment.shipper?.country?.toLowerCase() !== shipment.consignee?.country?.toLowerCase();

  const boxErrors: { [boxId: string]: string[] } = {};
  const productErrors: { [boxId: string]: { [prodId: string]: string[] } } = {};

  const boxesArray = p.box || [];
  boxesArray.forEach((box: any) => {
    const boxId = box.id || '0';
    const missing: string[] = [];
    const lengthNum = parseFloat(box.length);
    const widthNum = parseFloat(box.width);
    const heightNum = parseFloat(box.height);
    const weightNum = parseFloat(box.weight);

    if (isNaN(lengthNum) || lengthNum <= 0) missing.push('length');
    if (isNaN(widthNum) || widthNum <= 0) missing.push('width');
    if (isNaN(heightNum) || heightNum <= 0) missing.push('height');
    if (isNaN(weightNum) || weightNum <= 0) missing.push('weight');

    if (missing.length > 0) {
      boxErrors[boxId] = missing;
    }

    const productsArray = box.products || [];
    productsArray.forEach((prod: any, prodIdx: number) => {
      const prodId = prod.id || prodIdx.toString();
      const prodMissing = validateProductJoi(prod);
      if (isExport && !prod.HSNCode?.trim()) {
        prodMissing.push('HSNCode');
      }
      if (prodMissing.length > 0) {
        if (!productErrors[boxId]) productErrors[boxId] = {};
        productErrors[boxId][prodId] = prodMissing;
      }
    });
  });

  const isValid =
    shipperErrors.length === 0 &&
    consigneeErrors.length === 0 &&
    packageErrors.length === 0 &&
    Object.keys(boxErrors).length === 0 &&
    Object.keys(productErrors).length === 0;

  return {
    shipperErrors,
    consigneeErrors,
    packageErrors,
    boxErrors,
    productErrors,
    isValid,
  };
};

export const getPendingFields = (shipment: any): string[] => {
  const valResult = validateShipmentFields(shipment);
  const pending: string[] = [];

  // 1. Shipper fields
  if (valResult.shipperErrors.includes('name')) pending.push('Shipper Name');
  if (valResult.shipperErrors.includes('line1')) pending.push('Shipper Address');
  if (valResult.shipperErrors.includes('city')) pending.push('Shipper City');
  if (valResult.shipperErrors.includes('country')) pending.push('Shipper Country');
  if (valResult.shipperErrors.includes('pincode')) pending.push('Shipper Pincode');
  if (valResult.shipperErrors.includes('phoneNumber')) pending.push('Shipper Phone Number');
  if (valResult.shipperErrors.includes('telephoneCode')) pending.push('Shipper Telephone Code');
  if (valResult.shipperErrors.includes('altContactNo')) pending.push('Shipper Alternate Mobile No.');
  if (valResult.shipperErrors.includes('altContactTelephoneCode')) pending.push('Shipper Alternate Country Code');

  // 2. Consignee fields
  if (valResult.consigneeErrors.includes('name')) pending.push('Consignee Name');
  if (valResult.consigneeErrors.includes('line1')) pending.push('Consignee Address');
  if (valResult.consigneeErrors.includes('city')) pending.push('Consignee City');
  if (valResult.consigneeErrors.includes('country')) pending.push('Consignee Country');
  if (valResult.consigneeErrors.includes('pincode')) pending.push('Consignee Pincode');
  if (valResult.consigneeErrors.includes('phoneNumber')) pending.push('Consignee Phone Number');
  if (valResult.consigneeErrors.includes('telephoneCode')) pending.push('Consignee Telephone Code');
  if (valResult.consigneeErrors.includes('altContactNo')) pending.push('Consignee Alternate Mobile No.');
  if (valResult.consigneeErrors.includes('altContactTelephoneCode')) pending.push('Consignee Alternate Country Code');

  // 3. Package fields
  if (valResult.packageErrors.includes('unitOfMeasurement')) pending.push('Unit of Measurement');
  if (valResult.packageErrors.includes('currency')) pending.push('Currency');
  if (valResult.packageErrors.includes('shipmentMode')) pending.push('Shipment Mode');

  // 4. Box & Product fields
  const p = shipment.package || {};
  const boxesArray = p.box || [];

  if (boxesArray.length === 0) {
    pending.push('Boxes (At least 1 box is required)');
  } else {
    boxesArray.forEach((box: any, boxIdx: number) => {
      const boxId = box.id || '0';
      const boxErrs = valResult.boxErrors[boxId] || [];
      if (boxErrs.includes('length')) pending.push(`Box ${boxIdx + 1}: Length`);
      if (boxErrs.includes('width')) pending.push(`Box ${boxIdx + 1}: Width`);
      if (boxErrs.includes('height')) pending.push(`Box ${boxIdx + 1}: Height`);
      if (boxErrs.includes('weight')) pending.push(`Box ${boxIdx + 1}: Weight`);

      const prodErrsMap = valResult.productErrors[boxId] || {};
      const productsArray = box.products || [];

      if (productsArray.length === 0) {
        pending.push(`Box ${boxIdx + 1}: Products (At least 1 product is required)`);
      } else {
        productsArray.forEach((prod: any, prodIdx: number) => {
          const prodId = prod.id || prodIdx.toString();
          const prodErrs = prodErrsMap[prodId] || [];
          if (prodErrs.includes('description')) pending.push(`Box ${boxIdx + 1}, Product ${prodIdx + 1}: Description`);
          if (prodErrs.includes('quantity')) pending.push(`Box ${boxIdx + 1}, Product ${prodIdx + 1}: Quantity`);
          if (prodErrs.includes('unitValue')) pending.push(`Box ${boxIdx + 1}, Product ${prodIdx + 1}: Unit Value`);
          if (prodErrs.includes('HSNCode')) pending.push(`Box ${boxIdx + 1}, Product ${prodIdx + 1}: HSN Code`);
        });
      }
    });
  }

  return pending;
};

export const convertJobToBulkShipment = (job: any, countryList?: DropdownOptionType[]): BulkShipmentType => {
  const fields = job.fields || {};

  const getCountryName = (cName?: string) => {
    if (!cName) return '';
    const match = (countryList || []).find(
      c => c.value.toLowerCase() === cName.toLowerCase() || c.display.toLowerCase() === cName.toLowerCase(),
    );
    return match ? match.value : cName;
  };

  const cleanPhoneCode = (code?: string) => {
    if (!code) return '';
    const clean = code.trim();
    return clean.startsWith('+') ? clean : `+${clean}`;
  };

  const sanitizePhone = (num?: string): string => (num ? String(num).replace(/[^\d]/g, '') : '');

  const shipper = {
    name: fields.shipper?.name || fields.shipper?.companyName || '',
    companyName: fields.shipper?.companyName || '',
    line1: fields.shipper?.line1 || '',
    line2: fields.shipper?.line2 || '',
    city: fields.shipper?.city || '',
    state: fields.shipper?.state || '',
    country: getCountryName(fields.shipper?.country ? String(fields.shipper.country) : undefined),
    pincode: fields.shipper?.pincode || '',
    phoneNumber: sanitizePhone(fields.shipper?.phone?.number),
    telephoneCode: cleanPhoneCode(fields.shipper?.phone?.telephoneCode),
    email: fields.shipper?.email || '',
    altContactTelephoneCode: '',
    altContactNo: '',
    altContactName: '',
  };

  const consignee = {
    name: fields.consignee?.name || fields.consignee?.companyName || '',
    companyName: fields.consignee?.companyName || '',
    line1: fields.consignee?.line1 || '',
    line2: fields.consignee?.line2 || '',
    city: fields.consignee?.city || '',
    state: fields.consignee?.state || '',
    country: getCountryName(fields.consignee?.country ? String(fields.consignee.country) : undefined),
    pincode: fields.consignee?.pincode || '',
    phoneNumber: sanitizePhone(fields.consignee?.phone?.number),
    telephoneCode: cleanPhoneCode(fields.consignee?.phone?.telephoneCode),
    email: fields.consignee?.email || '',
    altContactTelephoneCode: '',
    altContactNo: '',
    altContactName: '',
  };

  const p = fields.package || {};

  // Parse boxes
  const parsedBoxes: BoxType[] = [];
  if (p.box && Array.isArray(p.box)) {
    p.box.forEach((b: any, index: number) => {
      const boxProducts = (b.products || []).map((prod: any, prodIndex: number) => {
        const rawQty = prod.quantity ?? prod.qty ?? prod.Quantity ?? prod.Qty;
        const quantity = rawQty !== undefined && rawQty !== null && rawQty !== '' ? Number(rawQty) : null;
        const rawUnitVal = prod.unitValue ?? prod.value ?? prod.UnitValue ?? prod.Value;
        const unitValue =
          rawUnitVal !== undefined && rawUnitVal !== null && rawUnitVal !== '' ? Number(rawUnitVal) : null;
        return {
          id: prodIndex.toString(),
          description: prod.description || prod.Description || '',
          quantity,
          unitValue,
          HSNName: prod.HSNName || prod.hsnName || '',
          HSNCode: prod.HSNCode ? String(prod.HSNCode) : prod.hsnCode ? String(prod.hsnCode) : '',
          productId: prod.productId ? String(prod.productId).trim() : '',
          productIdType: prod.productIdType ? String(prod.productIdType).trim() : 'SKU',
          merchantProductId: prod.merchantProductId ? String(prod.merchantProductId).trim() : '',
          standardManufacturerProductId: prod.standardManufacturerProductId
            ? String(prod.standardManufacturerProductId).trim()
            : '',
          nonStandardManufacturerProductId: prod.nonStandardManufacturerProductId
            ? String(prod.nonStandardManufacturerProductId).trim()
            : '',
          totalValue: quantity && unitValue ? quantity * unitValue : 0,
        };
      });

      parsedBoxes.push({
        id: index.toString(),
        length: b.length ? String(b.length) : '',
        width: b.width ? String(b.width) : '',
        height: b.height ? String(b.height) : '',
        weight: b.weight ? String(b.weight) : '',
        boxCopyCount: b.boxCopyCount !== undefined ? Number(b.boxCopyCount) : 0,
        products: boxProducts,
      });
    });
  }

  // If no boxes are parsed, add a default empty box to allow editing
  if (parsedBoxes.length === 0) {
    parsedBoxes.push({
      id: '0',
      length: '',
      width: '',
      height: '',
      weight: '',
      boxCopyCount: 0,
      products: [],
    });
  }

  const totalBoxWeight = parsedBoxes.reduce((prevW, currW) => prevW + (parseFloat(String(currW.weight || 0)) || 0), 0);

  return {
    isSelected: true,
    carrierPreference: 'cheapest',
    shipper,
    consignee,
    package: {
      unitOfMeasurement: p.unitOfMeasurement || '',
      packaging: typeof p.packaging === 'boolean' ? p.packaging : false,
      insurance: typeof p.insurance === 'boolean' ? p.insurance : false,
      isDocument: typeof p.isDocument === 'boolean' ? p.isDocument : false,
      currency: p.currency || '',
      shipmentMode: fields.shipmentMode || p.shipmentMode || '',
      isCod: typeof p.isCod === 'boolean' ? p.isCod : false,
      isDropOff: typeof p.isDropOff === 'boolean' ? p.isDropOff : false,
      pickupDate: dayjs(),
      timeSlot: '',
      codAmount: p.codAmount ? Number(p.codAmount) : 0,
      box: parsedBoxes,
      totalBoxWeight,
      invoiceNumber: p.invoiceNumber || fields.invoiceNumber || '',
      userReferenceNumber: fields.userReferenceNumber || '',
    },
  };
};

export const formatDecimal = (val: any) => {
  if (val === undefined || val === null || val === '') return '—';
  const num = Number(val);
  if (isNaN(num)) return String(val);
  return num.toFixed(2);
};
